/**
 * The undo stack's operations: what a step IS, and how to apply or invert one.
 *
 * Pure, and moved out of `task-workspace-page` so that the stack can be driven by something other
 * than a 2,200-line page component — which is what `use-undo-stack` and the sequence harness need.
 * Nothing here touches React, the DOM or the network: an op plus a state goes in, a state comes
 * out, and inverting an op twice gives it back.
 *
 * The contract these serve is in `docs/undo-redo-contract.md`.
 */
import { composeSetName, setLeaf } from '@agavi/blocks';
import { arrayMove } from '@dnd-kit/sortable';

import type { Block, TaskVariable } from '@/types/experience';
import type { TaskFlowConfig, AnswerRefAlias } from '@agavi/blocks';

import { renameVarInBlockData, renameVarInFormula } from '@/components/task/variable-utils';



/**
 * The four things an op is applied to.
 *
 * Lives here rather than in `use-undo-stack` so `applyOp` can name it without importing the hook
 * that calls it. Re-exported from there, where every caller already looks for it.
 */
export interface StackState {
  title: string;
  blocks: Block[];
  variables: TaskVariable[];
  /**
   * The EXPERIENCE's variables, which live in another document and are undone all the same.
   *
   * They are edited in the same panel as the task's and look identical there, so a teacher has no
   * way to know which of two adjacent rows is undoable — which is the whole argument for this
   * being here rather than being the third exemption from the stack.
   */
  experienceVariables: TaskVariable[];
  /**
   * The experience's ANSWER-REFERENCE ALIASES, here for the same reason the variables above are.
   *
   * They live in another document and are edited from inside the task — a chip is renamed in
   * the editor, a member is added in the References panel — so a teacher has no way to know
   * these particular edits are the ones history forgot. They were briefly outside the stack:
   * moving re-point onto the alias made it ONE write instead of N, which was the right change
   * and silently cost it its undo, because the stack was block-shaped.
   */
  answerRefAliases: AnswerRefAlias[];
  flow: TaskFlowConfig;
}

/**
 * Which list a variable op is about.
 *
 * Absent means the task's. Undo stacks are persisted (`agavi.undoLog`), so an op written before
 * this existed has no scope and must keep meaning what it meant.
 */
export type VariableScope = 'task' | 'experience';

export type Op =
  | { type: 'add-block';        label: string; block: Block; idx: number; aliases?: AnswerRefAlias[] }
  /**
   * `aliases` are the names the deletion ORPHANED, carried so one undo restores both.
   *
   * A block holds chips, and deleting it leaves their names cited by nothing. Those names
   * are swept — correctly — but as a step of their own, so undoing one gesture took two
   * presses: "first one undeletes the aliases, then i have to click again to bring the block
   * back". `paste-blocks` already carries aliases for exactly this reason; this is the same
   * bargain in the other direction.
   *
   * Absent where the deletion orphaned nothing, which is the common case.
   */
  | { type: 'remove-block';     label: string; block: Block; idx: number; aliases?: AnswerRefAlias[] }
  | { type: 'move-block';       label: string; from: number; to: number }
  | { type: 'duplicate-block';  label: string; block: Block; idx: number }
  // Paste is ONE op even though it changes two things, because a paste that imported a variable
  // and then got half-undone would leave a block bound to a variable that is no longer there.
  /** `aliases` are the ones the paste ADOPTED, the same difference `variables` holds: a
   *  same-experience paste already holds them all and adopts none, so the field is usually empty. */
  | { type: 'paste-blocks';     label: string; blocks: Block[]; idx: number; variables: TaskVariable[]; aliases?: AnswerRefAlias[] }
  | { type: 'unpaste-blocks';   label: string; blocks: Block[]; idx: number; variables: TaskVariable[]; aliases?: AnswerRefAlias[] }
  | { type: 'update-block';     label: string; blockId: string; prev: Record<string, unknown>; next: Record<string, unknown>;
      /** Which fields moved, as `data-field-id` values — computed once when the op is pushed rather
       *  than re-diffed on every undo. Empty when nothing in the data is addressable. */
      fields?: string[] }
  /**
   * ONE EDIT THAT MOVED SEVERAL BLOCKS, undone as one.
   *
   * Deleting a leader's variant (§7.3) takes the matching answer row out of every follower, so
   * the leader's change and the followers' are one decision. As separate `update-block` ops a
   * single undo would restore the variant and leave the answers gone — the worst half of both.
   *
   * No `fields`: the editor ring points at what moved in ONE block's editor, and this op
   * deliberately moves blocks the teacher is not looking at.
   */
  | { type: 'update-blocks';    label: string; entries: { blockId: string; prev: Record<string, unknown>; next: Record<string, unknown> }[] }
  | { type: 'update-title';     label: string; prev: string; next: string }
  | { type: 'add-variable';     label: string; variable: TaskVariable; idx: number; scope?: VariableScope }
  | { type: 'remove-variable';  label: string; variable: TaskVariable; idx: number; scope?: VariableScope }
  /** `coalesce` as on `update-flow`: an equation or a range is typed, so without it the stack
   *  fills with one step per letter. A RENAME leaves it undefined — it commits once on blur and
   *  sweeps every chip in the task, which is emphatically its own step. */
  | { type: 'update-variable';  label: string; variableId: string; prev: TaskVariable; next: TaskVariable; scope?: VariableScope; coalesce?: string }
  /**
   * A whole SET added or removed — a duplicate, or a delete of the set and everything in it.
   *
   * One op rather than one per member, for the reason the rename above gives: a teacher who
   * pressed one button expects one press of undo to take it back. Deleting `starA` as five
   * `remove-variable` steps also undoes in reverse dependency order, so the four intermediate
   * states each have an equation standing on a member that is not back yet.
   */
  | { type: 'add-set';          label: string; set: string; variables: TaskVariable[]; idx: number; scope?: VariableScope }
  | { type: 'remove-set';       label: string; set: string; variables: TaskVariable[]; idx: number; scope?: VariableScope }
  /**
   * A whole SET renamed: `starA` to `starB`, and with it every member's composed name.
   *
   * One op rather than one per member, because a set rename is one thing a teacher did and one
   * thing they expect to take back. The members are read out of the state rather than recorded
   * on the op, which keeps this symmetric for free: `invertOp` swaps `from` and `to`, and at
   * that point the members are the ones sitting in `to`.
   */
  | { type: 'rename-set';       label: string; from: string; to: string; scope?: VariableScope }
  /**
   * The whole flow config, before and after.
   *
   * A SNAPSHOT rather than one op per kind of flow edit, unlike blocks and variables. The config is
   * a few kilobytes and every edit to it is a whole-object replacement already, so eleven op types
   * would buy nothing but eleven inverses to keep honest. What the granular ops really provide is a
   * good label, and `label` supplies that directly — the caller says "Added event", not the type.
   *
   * `coalesce` merges a run of edits to the same thing into one step. Typing a rule's title fires an
   * onChange per keystroke, and without this the stack fills with one step per letter. Structural
   * edits leave it undefined and always get their own step.
   */
  | { type: 'update-flow';      label: string; prev: TaskFlowConfig; next: TaskFlowConfig; coalesce?: string }
  /**
   * The whole alias list, before and after — a SNAPSHOT op, like `update-flow`.
   *
   * Per-item ops the way variables have them would need four of them (rename, repoint, add
   * member, remove member) to express edits that are all already "replace the list". The list
   * is small, every mutation rewrites it wholesale, and a snapshot inverts by swapping two
   * fields. `coalesce` so typing a name is one step rather than one per keystroke.
   */
  | { type: 'update-aliases';   label: string; prev: AnswerRefAlias[]; next: AnswerRefAlias[]; coalesce?: string }

/**
 * Takes the whole state and returns the whole state, rather than four positional pieces.
 *
 * It used to be `(op, title, blocks, variables)`, which was fine at three and became a trap at four:
 * every case had to restate the pieces it was NOT changing, so adding one meant editing eleven
 * returns and hoping none was missed. Spreading says "everything else is untouched" once.
 */
/**
 * The cross-task sweep an undo or redo still owes, or null when it owes none.
 *
 * Split out of the page so the direction can be TESTED. It is the one part of applying an op
 * that cannot live in `applyOp`: an experience variable's rename rewrote blocks in tasks that
 * are not loaded, over the network, and a pure reducer over `StackState` can only put back what
 * is in front of the teacher.
 *
 * Undo runs the INVERTED op, so the name travels next -> prev; redo runs it as recorded.
 * Task-scoped renames are absent because their blocks are all in `StackState` already.
 */
export function crossTaskRenameFor(
  op: Op,
  direction: 'undo' | 'redo',
): { variableId: string; from: string; to: string } | null {
  if (op.type !== 'update-variable' || op.scope !== 'experience') return null;
  const { name: prev } = op.prev, { name: next } = op.next;
  // A variable gaining or losing its name is not a rename: there are no chips to sweep on one
  // side of it. The live path makes the same distinction.
  if (!prev || !next || prev === next) return null;
  return direction === 'undo'
    ? { variableId: op.variableId, from: next, to: prev }
    : { variableId: op.variableId, from: prev, to: next };
}

export function applyOp(op: Op, state: StackState): StackState {
  const { blocks, variables } = state;
  /** Which list a variable op reads and writes — the task's unless it says otherwise. */
  const varKey = (o: { scope?: VariableScope }): 'variables' | 'experienceVariables' =>
    (o.scope === 'experience' ? 'experienceVariables' : 'variables');
  switch (op.type) {
    case 'add-block':
    case 'duplicate-block': {
      const back = op.type === 'add-block' ? op.aliases : undefined;
      return {
        ...state,
        blocks: [...blocks.slice(0, op.idx), op.block, ...blocks.slice(op.idx)].map((b, i) => ({ ...b, order: i })),
        /* And back again on undo, before the block that cites them — a chip restored onto a
           dead name is the state the sweep's own two-step ordering was avoiding. */
        answerRefAliases: back?.length
          ? [...state.answerRefAliases.filter(a => !back.some(g => g.id === a.id)), ...back]
          : state.answerRefAliases,
      };
    }
    case 'remove-block':
      return {
        ...state,
        blocks: blocks.filter(b => b.id !== op.block.id).map((b, i) => ({ ...b, order: i })),
        /* The names the block's chips were the last to cite, going with it. */
        answerRefAliases: op.aliases?.length
          ? state.answerRefAliases.filter(a => !op.aliases!.some(g => g.id === a.id))
          : state.answerRefAliases,
      };
    case 'move-block':
      return { ...state, blocks: arrayMove(blocks, op.from, op.to).map((b, i) => ({ ...b, order: i })) };
    case 'paste-blocks': {
      const ids = new Set(op.variables.map(v => v.id));
      const aliasIds = new Set((op.aliases ?? []).map(a => a.id));
      return {
        ...state,
        blocks: [...blocks.slice(0, op.idx), ...op.blocks, ...blocks.slice(op.idx)].map((b, i) => ({ ...b, order: i })),
        // Re-adding on redo must not double up variables a previous redo already restored.
        variables: [...variables.filter(v => !ids.has(v.id)), ...op.variables],
        // Referentially unchanged when nothing was adopted, which is what lets the page skip the
        // write: `applyState` compares the array by identity before PATCHing another document.
        answerRefAliases: aliasIds.size === 0 ? state.answerRefAliases
          : [...state.answerRefAliases.filter(a => !aliasIds.has(a.id)), ...(op.aliases ?? [])],
      };
    }
    case 'unpaste-blocks': {
      const blockIds = new Set(op.blocks.map(b => b.id));
      const varIds = new Set(op.variables.map(v => v.id));
      const aliasIds = new Set((op.aliases ?? []).map(a => a.id));
      return {
        ...state,
        blocks: blocks.filter(b => !blockIds.has(b.id)).map((b, i) => ({ ...b, order: i })),
        variables: variables.filter(v => !varIds.has(v.id)),
        answerRefAliases: aliasIds.size === 0 ? state.answerRefAliases
          : state.answerRefAliases.filter(a => !aliasIds.has(a.id)),
      };
    }
    case 'update-block':
      return { ...state, blocks: blocks.map(b => b.id === op.blockId ? { ...b, data: op.next } : b) };
    case 'update-blocks': {
      const byId = new Map(op.entries.map(e => [e.blockId, e.next]));
      return { ...state, blocks: blocks.map(b => byId.has(b.id) ? { ...b, data: byId.get(b.id)! } : b) };
    }
    case 'update-title':
      return { ...state, title: op.next };
    case 'add-variable': {
      const list = state[varKey(op)];
      return { ...state, [varKey(op)]: [...list.slice(0, op.idx), op.variable, ...list.slice(op.idx)] };
    }
    case 'remove-variable':
      return { ...state, [varKey(op)]: state[varKey(op)].filter(v => v.id !== op.variable.id) };
    case 'update-variable': {
      const swapped = { ...state, [varKey(op)]: state[varKey(op)].map(v => v.id === op.variableId ? op.next : v) };
      if (!op.prev.name || !op.next.name || op.prev.name === op.next.name) return swapped;
      /*
       * A RENAME is not a change to one row. Chips store the NAME — a formula holds `{river}`, a
       * richtext chip holds `data-variable-chip="river"` — so the live edit rewrites every block
       * that mentions it, and until now undo put the row's name back and left all of them saying
       * the new name, pointing at a variable that no longer existed.
       *
       * Doing it here rather than recording the rewrites on the op keeps one description of what a
       * rename IS, and makes undo and redo symmetric for free: `invertOp` swaps prev and next, so
       * the same code sweeps the other way. It is safe to replay because renaming is idempotent —
       * sweeping `river`→`stream` over data that already says `stream` changes nothing.
       *
       * This puts back only what `StackState` holds — this task's blocks and the two variable
       * lists. An EXPERIENCE variable's rename also swept tasks that are not loaded, over the
       * network, and that half cannot happen in a pure reducer: the page re-runs it from
       * `onApplied`, which is the one place in the undo path allowed to have effects.
       */
      const from = op.prev.name, to = op.next.name;
      const renameIn = (list: TaskVariable[]) => list.map(v =>
        v.formula ? { ...v, formula: renameVarInFormula(v.formula, from, to) } : v);
      return {
        ...swapped,
        blocks: swapped.blocks.map(b => ({
          ...b, data: renameVarInBlockData(b.data as Record<string, unknown>, from, to, op.variableId),
        })),
        variables: renameIn(swapped.variables),
        experienceVariables: renameIn(swapped.experienceVariables),
      };
    }
    case 'add-set': {
      const list = state[varKey(op)];
      return { ...state, [varKey(op)]: [...list.slice(0, op.idx), ...op.variables, ...list.slice(op.idx)] };
    }
    case 'remove-set': {
      const gone = new Set(op.variables.map(v => v.id));
      return { ...state, [varKey(op)]: state[varKey(op)].filter(v => !gone.has(v.id)) };
    }
    case 'rename-set': {
      /*
       * Every member renamed, and every chip and sibling formula swept with them.
       *
       * The sweep is the same one a single rename does, run once per member, and for the same
       * reason: chips store the NAME, so a row whose name changed without them is a chip
       * pointing at a variable that no longer exists.
       *
       * Safe to replay, because each pair's rename is idempotent — sweeping `starA_mass` to
       * `starB_mass` over data that already says `starB_mass` changes nothing.
       */
      const key = varKey(op);
      const pairs = state[key]
        .filter(v => v.set === op.from && v.name)
        .map(v => [v.name, composeSetName(op.to, setLeaf(v))] as const);

      /*
       * `owns` separates the two halves of this, which are not the same job.
       *
       * Only the list the set LIVES in has its rows renamed. But BOTH lists get their formulas
       * swept, because an equation in either scope can stand on a member — a task equation
       * reading `{starA_mass}` from the experience is the ordinary case, not a corner one.
       */
      const renameIn = (list: TaskVariable[], owns: boolean) => list.map(v => {
        let next = owns && v.set === op.from
          ? { ...v, set: op.to, name: composeSetName(op.to, setLeaf(v)) }
          : v;
        for (const [from, to] of pairs) {
          if (next.formula) next = { ...next, formula: renameVarInFormula(next.formula, from, to) };
        }
        return next;
      });

      let blocks = state.blocks;
      for (const [from, to] of pairs) {
        blocks = blocks.map(b => ({
          ...b, data: renameVarInBlockData(b.data as Record<string, unknown>, from, to),
        }));
      }

      return {
        ...state,
        blocks,
        variables: renameIn(state.variables, key === 'variables'),
        experienceVariables: renameIn(state.experienceVariables, key === 'experienceVariables'),
      };
    }
    case 'update-flow':
      return { ...state, flow: op.next };
    case 'update-aliases':
      return { ...state, answerRefAliases: op.next };
  }
}

export function invertOp(op: Op): Op {
  switch (op.type) {
    case 'add-block':
      return { type: 'remove-block',    label: op.label, block: op.block, idx: op.idx, aliases: op.aliases };
    case 'remove-block':
      return { type: 'add-block',       label: op.label, block: op.block, idx: op.idx, aliases: op.aliases };
    case 'duplicate-block':
      return { type: 'remove-block',    label: op.label, block: op.block, idx: op.idx };
    case 'paste-blocks':
      return { type: 'unpaste-blocks',  label: op.label, blocks: op.blocks, idx: op.idx, variables: op.variables, aliases: op.aliases };
    case 'unpaste-blocks':
      return { type: 'paste-blocks',    label: op.label, blocks: op.blocks, idx: op.idx, variables: op.variables, aliases: op.aliases };
    case 'move-block':
      return { ...op, from: op.to, to: op.from };
    case 'update-block':
      return { ...op, prev: op.next, next: op.prev };
    case 'update-blocks':
      return { ...op, entries: op.entries.map(e => ({ ...e, prev: e.next, next: e.prev })) };
    case 'update-title':
      return { ...op, prev: op.next, next: op.prev };
    case 'add-variable':
      return { type: 'remove-variable', label: op.label, variable: op.variable, idx: op.idx, scope: op.scope };
    case 'remove-variable':
      return { type: 'add-variable',    label: op.label, variable: op.variable, idx: op.idx, scope: op.scope };
    case 'update-variable':
      return { ...op, prev: op.next, next: op.prev };
    case 'add-set':
      return { type: 'remove-set', label: op.label, set: op.set, variables: op.variables, idx: op.idx, scope: op.scope };
    case 'remove-set':
      return { type: 'add-set',    label: op.label, set: op.set, variables: op.variables, idx: op.idx, scope: op.scope };
    case 'rename-set':
      return { ...op, from: op.to, to: op.from };
    case 'update-flow':
      return { ...op, prev: op.next, next: op.prev };
    case 'update-aliases':
      return { ...op, prev: op.next, next: op.prev };
  }
}